import { useCallback, useEffect, useState } from 'react'
import { Square } from 'lucide-react'
import { compareApi } from '@/lib/api/compare'
import { ratingsApi } from '@/lib/api/ratings'
import {
  abortCompareStream,
  getCompareStreamBatch,
  isCompareStreamActive,
  subscribeCompareStream,
} from '@/lib/compare/stream-store'
import { ExportModal } from '@/components/export/ExportModal'
import { Button } from '@/components/ui/button'
import {
  getCompareColumnClassName,
  useCompareLayoutMode,
} from '@/hooks/useCompareLayoutMode'
import type { CompareBatch, CreateRatingRequest } from '@/types/compare'
import { CompareCard } from '@/components/compare/CompareCard'
import { CompareCardStack } from '@/components/compare/CompareCardStack'
import { cn } from '@/lib/utils'

function gridClassForMode(mode: ReturnType<typeof useCompareLayoutMode>, count: number): string {
  switch (mode) {
    case 'desktop':
      if (count >= 4) return 'grid grid-cols-4 gap-3'
      if (count === 3) return 'grid grid-cols-3 gap-3'
      return 'grid grid-cols-2 gap-3'
    case 'tablet-landscape':
      return count >= 3 ? 'grid grid-cols-3 gap-3' : 'grid grid-cols-2 gap-3'
    case 'tablet-portrait':
      return 'flex gap-3 overflow-x-auto pb-2'
    case 'phone-landscape':
      return 'grid grid-cols-2 gap-3'
    default:
      return 'grid grid-cols-1 gap-3'
  }
}

interface CompareLayoutProps {
  batchId: string
}

export function CompareLayout({ batchId }: CompareLayoutProps) {
  const layoutMode = useCompareLayoutMode()
  const [batch, setBatch] = useState<CompareBatch | null>(null)
  const [loading, setLoading] = useState(true)
  const [exportOpen, setExportOpen] = useState(false)

  const loadBatch = useCallback(async () => {
    setLoading(true)
    try {
      const data = await compareApi.getBatch(batchId)
      setBatch(data)
    } finally {
      setLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    const streamingBatch = getCompareStreamBatch(batchId)
    if (streamingBatch) {
      setBatch(streamingBatch)
      setLoading(false)
    }

    if (isCompareStreamActive(batchId)) {
      return subscribeCompareStream(batchId, (nextBatch) => {
        setBatch(nextBatch)
        setLoading(false)
      })
    }

    void loadBatch()
  }, [batchId, loadBatch])

  const handleSubmitRating = useCallback(
    async (request: CreateRatingRequest) => {
      const rating = await ratingsApi.createRating(request)
      const updated = await compareApi.updateResponseRating(batchId, request.modelResponseId, {
        id: rating.id,
        modelResponseId: rating.modelResponseId,
        ratingStars: rating.ratingStars,
        thumbs: rating.thumbs,
        isBestAnswer: rating.isBestAnswer,
        comment: rating.comment,
        createdAt: rating.createdAt,
      })

      if (updated) setBatch(updated)
    },
    [batchId],
  )

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading compare batch...</div>
  }

  if (!batch) {
    return <div className="p-4 text-sm text-muted-foreground">Compare batch not found.</div>
  }

  const isStreaming = isCompareStreamActive(batchId)

  const columnClass = getCompareColumnClassName(layoutMode)

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="compare-layout">
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h1 className="text-lg font-semibold">{batch.title ?? 'Compare'}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Prompt:</span> {batch.promptText}
            {isStreaming ? (
              <span className="ml-2 text-xs text-primary" data-testid="compare-streaming-indicator">
                Streaming…
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStreaming ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => abortCompareStream(batchId)}
              data-testid="compare-stop-button"
            >
              <Square className="h-4 w-4" />
              Stop
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExportOpen(true)}
            data-testid="compare-export-button"
          >
            Export
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {layoutMode === 'phone-portrait' ? (
          <CompareCardStack responses={batch.responses} onSubmitRating={handleSubmitRating} />
        ) : (
          <div
            className={cn(gridClassForMode(layoutMode, batch.responses.length), 'min-h-0')}
            data-testid="compare-columns-grid"
          >
            {batch.responses.map((response) => (
              <CompareCard
                key={response.modelResponseId}
                response={response}
                className={columnClass}
                onSubmitRating={handleSubmitRating}
              />
            ))}
          </div>
        )}
      </div>

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        exportType="compare_batch"
        sourceId={batch.id}
        title={batch.title ?? 'Compare batch'}
      />
    </div>
  )
}
